import AppPageCarded from "@app-core/AppPageCarded"
import {LoadingScreen} from "@app-core/LoadingScreen"
import React, {useCallback, useEffect, useState} from "react"
import {useAuth} from "auth/AuthContext"
import {useNavigate} from "react-router-dom"
import {EligibilityErrorScreen} from "components/EligibilityErrorScreen"
import {PaymentOption} from "components/PaymentOption"
import {paymentApi, productApi} from "data/api"
import {useEligibilityApplication} from "hooks/useEligibilityApplication"
import {useMessages} from "hooks/useMessages"
import {IProduct} from "models/IProduct"
import {retryAsync} from "utils/retry"

export const ChoosePlanView: React.FC = () => {
  const {user} = useAuth()
  const {eligibilityApplication} = useEligibilityApplication()
  const navigate = useNavigate()
  const {addMessage} = useMessages()
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const [products, setProducts] = useState<IProduct[]>([])

  if (!user || !eligibilityApplication) {
    navigate("/")
  }

  // Loads plan options with retry so a transient failure (the intermittent
  // "Issue Loading Plan Options" error) doesn't dead-end the user. Re-runnable
  // by the error screen's "Try Again" button.
  const loadProducts = useCallback(async () => {
    setHasError(false)
    setIsLoading(true)
    try {
      const results = await retryAsync(() => productApi.getProducts())
      setProducts(results)
      setIsLoading(false)
    } catch (error) {
      setHasError(true)
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!user || !eligibilityApplication) {
      navigate("/")
    } else {
      loadProducts()
    }
  }, [eligibilityApplication, loadProducts, navigate, user])

  const onSelectPlan = async (plan: string) => {
    if (eligibilityApplication) {
      try {
        setIsLoading(true)

        const url = await retryAsync(() =>
          paymentApi.createCheckoutSession({
            receipt_email: user?.email || "",
            selectedSections: eligibilityApplication?.diagnoses || [],
            applicationProcess: eligibilityApplication.applicationProcess,
            allowRepresentativeContact:
              eligibilityApplication.allowRepresentativeContact,
            plan,
            priceId: products.find((p) => p.planName === plan)?.priceId || ""
          })
        )

        window.location.href = url
      } catch (error) {
        addMessage({
          content: "Something went wrong. Please try again.",
          severity: "error"
        })
        setIsLoading(false)
      }
    }
  }

  if (isLoading) return <LoadingScreen isLoading={isLoading} />

  return (
    <AppPageCarded scroll="page">
      {hasError ? (
        <EligibilityErrorScreen onTryAgain={loadProducts} />
      ) : (
        <PaymentOption
          onSelectPaymentOption={onSelectPlan}
          products={products}
        />
      )}
    </AppPageCarded>
  )
}

export default ChoosePlanView
